"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.GridZones = void 0;
const MGRSConstants_1 = require("../MGRSConstants");
const MGRSUtils_1 = require("../MGRSUtils");
const BandLetterRange_1 = require("./BandLetterRange");
const GridRange_1 = require("./GridRange");
const GridZone_1 = require("./GridZone");
const LatitudeBand_1 = require("./LatitudeBand");
const LongitudinalStrip_1 = require("./LongitudinalStrip");
const ZoneNumberRange_1 = require("./ZoneNumberRange");
/**
 * Grid Zones, Longitudinal Strips, and Latitude Bands
 */
class GridZones {
    /**
     * Get the longitudinal strip by zone number
     *
     * @param zoneNumber
     *            zone number
     * @return longitudinal strip
     */
    static getLongitudinalStrip(zoneNumber) {
        MGRSUtils_1.MGRSUtils.validateZoneNumber(zoneNumber);
        return this.strips.get(zoneNumber);
    }
    /**
     * Get the west longitude in degrees of the zone number
     *
     * @param zoneNumber
     *            zone number
     * @return longitude in degrees
     */
    static getWestLongitude(zoneNumber) {
        return this.getLongitudinalStrip(zoneNumber).getWest();
    }
    /**
     * Get the east longitude in degrees of the zone number
     *
     * @param zoneNumber
     *            zone number
     * @return longitude in degrees
     */
    static getEastLongitude(zoneNumber) {
        return this.getLongitudinalStrip(zoneNumber).getEast();
    }
    /**
     * Get the latitude band by band letter
     *
     * @param bandLetter
     *            band letter
     * @return latitude band
     */
    static getLatitudeBand(bandLetter) {
        MGRSUtils_1.MGRSUtils.validateBandLetter(bandLetter);
        return this.bands.get(bandLetter);
    }
    /**
     * Get the south latitude in degrees of the band letter
     *
     * @param bandLetter
     *            band letter
     * @return latitude in degrees
     */
    static getSouthLatitude(bandLetter) {
        return this.getLatitudeBand(bandLetter).getSouth();
    }
    /**
     * Get the north latitude in degrees of the band letter
     *
     * @param bandLetter
     *            band letter
     * @return latitude in degrees
     */
    static getNorthLatitude(bandLetter) {
        return this.getLatitudeBand(bandLetter).getNorth();
    }
    /**
     * Get the zones within the bounds
     *
     * @param bounds
     *            bounds
     * @return grid zones
     */
    static getZones(bounds) {
        const zones = [];
        const gridRange = this.getGridRange(bounds);
        for (const zone of gridRange) {
            zones.push(zone);
        }
        return zones;
    }
    /**
     * Get the grid zone by zone number and band letter
     *
     * @param zoneNumber
     *            zone number
     * @param bandLetter
     *            band letter
     * @return grid zone
     */
    static getGridZone(zoneNumber, bandLetter) {
        MGRSUtils_1.MGRSUtils.validateZoneNumber(zoneNumber);
        MGRSUtils_1.MGRSUtils.validateBandLetter(bandLetter);
        return this.gridZones.get(zoneNumber).get(bandLetter);
    }
    /**
     * Get the grid zone by MGRS
     *
     * @param mgrs
     *            mgrs coordinate
     * @return grid zone
     */
    static getGridZoneFromMGRS(mgrs) {
        return this.getGridZone(mgrs.getZone(), mgrs.getBand());
    }
    /**
     * Get a grid range from the bounds
     *
     * @param bounds
     *            bounds
     * @return grid range
     */
    static getGridRange(bounds) {
        bounds = bounds.toDegrees();
        const zoneNumberRange = this.getZoneNumberRangeFromBounds(bounds);
        const bandLetterRange = this.getBandLetterRangeFromBounds(bounds);
        return new GridRange_1.GridRange(zoneNumberRange, bandLetterRange);
    }
    /**
     * Get a zone number range between the western and eastern bounds
     *
     * @param bounds
     *            bounds
     * @return zone number range
     */
    static getZoneNumberRangeFromBounds(bounds) {
        bounds = bounds.toDegrees();
        return this.getZoneNumberRange(bounds.getWest(), bounds.getEast());
    }
    /**
     * Get a zone number range between the western and eastern longitudes
     *
     * @param west
     *            western longitude in degrees
     * @param east
     *            eastern longitude in degrees
     * @return zone number range
     */
    static getZoneNumberRange(west, east) {
        const westZone = this.getZoneNumberFromLongitude(west, false);
        const eastZone = this.getZoneNumberFromLongitude(east, true);
        return new ZoneNumberRange_1.ZoneNumberRange(westZone, eastZone);
    }
    /**
     * Get the zone number of the point
     *
     * @param point
     *            point
     * @return zone number
     */
    static getZoneNumberFromPoint(point) {
        point = point.toDegrees();
        return this.getZoneNumber(point.getLongitude(), point.getLatitude());
    }
    /**
     * Get the zone number of the longitude and latitude
     *
     * @param longitude
     *            longitude
     * @param latitude
     *            latitude
     * @return zone number
     */
    static getZoneNumber(longitude, latitude) {
        let zoneNumber = this.getZoneNumberFromLongitude(longitude);
        const svalbardZone = this.isSvalbardZone(zoneNumber);
        const norwayZone = this.isNorwayZone(zoneNumber);
        if (svalbardZone || norwayZone) {
            const bandLetter = this.getBandLetterFromLatitude(latitude);
            if (svalbardZone && this.isSvalbardLetter(bandLetter)) {
                zoneNumber = this.getSvalbardZone(longitude);
            }
            else if (norwayZone && this.isNorwayLetter(bandLetter)) {
                zoneNumber = this.getNorwayZone(longitude);
            }
        }
        return zoneNumber;
    }
    /**
     * Get the zone number of the longitude (degrees between
     * {@link MGRSConstants#MIN_LON} and {@link MGRSConstants#MAX_LON})
     *
     * @param longitude
     *            longitude in degrees
     * @param eastern
     *            true for eastern number on edges, false for western
     * @return zone number
     */
    static getZoneNumberFromLongitude(longitude, eastern = true) {
        // Normalize the longitude if needed
        if (longitude < MGRSConstants_1.MGRSConstants.MIN_LON || longitude > MGRSConstants_1.MGRSConstants.MAX_LON) {
            longitude = ((longitude - MGRSConstants_1.MGRSConstants.MIN_LON) % (2 * MGRSConstants_1.MGRSConstants.MAX_LON)) + MGRSConstants_1.MGRSConstants.MIN_LON;
        }
        // Determine the zone
        const zoneValue = (longitude - MGRSConstants_1.MGRSConstants.MIN_LON) / MGRSConstants_1.MGRSConstants.ZONE_WIDTH;
        let zoneNumber = 1 + ~~zoneValue;
        // Handle western edge cases and 180.0
        if (!eastern) {
            if (zoneNumber > 1 && zoneValue % 1.0 === 0.0) {
                zoneNumber--;
            }
        }
        else if (zoneNumber > MGRSConstants_1.MGRSConstants.MAX_ZONE_NUMBER) {
            zoneNumber--;
        }
        return zoneNumber;
    }
    /**
     * Get a band letter range between the southern and northern bounds
     *
     * @param bounds
     *            bounds
     * @return band letter range
     */
    static getBandLetterRangeFromBounds(bounds) {
        bounds = bounds.toDegrees();
        return this.getBandLetterRange(bounds.getSouth(), bounds.getNorth());
    }
    /**
     * Get a band letter range between the southern and northern latitudes in
     * degrees
     *
     * @param south
     *            southern latitude in degrees
     * @param north
     *            northern latitude in degrees
     * @return band letter range
     */
    static getBandLetterRange(south, north) {
        const southLetter = this.getBandLetterFromLatitude(south, false);
        const northLetter = this.getBandLetterFromLatitude(north, true);
        return new BandLetterRange_1.BandLetterRange(southLetter, northLetter);
    }
    /**
     * Get the band letter of the latitude (degrees between
     * {@link MGRSConstants#MIN_LAT} and {@link MGRSConstants#MAX_LAT})
     *
     * @param latitude
     *            latitude in degrees
     * @param northern
     *            true for northern band on edges, false for southern
     * @return band letter
     */
    static getBandLetterFromLatitude(latitude, northern = true) {
        // Bound the latitude if needed
        if (latitude < MGRSConstants_1.MGRSConstants.MIN_LAT) {
            latitude = MGRSConstants_1.MGRSConstants.MIN_LAT;
        }
        else if (latitude > MGRSConstants_1.MGRSConstants.MAX_LAT) {
            latitude = MGRSConstants_1.MGRSConstants.MAX_LAT;
        }
        const bandValue = (latitude - MGRSConstants_1.MGRSConstants.MIN_LAT) / MGRSConstants_1.MGRSConstants.BAND_HEIGHT;
        let bands = ~~bandValue;
        // Handle 80.0 to 84.0 and southern edge cases
        if (bands >= MGRSConstants_1.MGRSConstants.NUM_BANDS) {
            bands--;
        }
        else if (!northern && bands > 0 && bandValue % 1.0 === 0.0) {
            bands--;
        }
        // Handle skipped 'I' and 'O' letters
        if (bands > 10) {
            bands += 2;
        }
        else if (bands > 5) {
            bands++;
        }
        let letter = MGRSConstants_1.MGRSConstants.MIN_BAND_LETTER.codePointAt(0);
        if (letter) {
            letter += bands;
        }
        return String.fromCharCode(letter);
    }
    /**
     * Is the zone number and band letter a Svalbard GZD (31X - 37X)
     *
     * @param zoneNumber
     *            zone number
     * @param bandLetter
     *            band letter
     * @return true if a Svalbard GZD
     */
    static isSvalbard(zoneNumber, bandLetter) {
        return this.isSvalbardLetter(bandLetter) && this.isSvalbardZone(zoneNumber);
    }
    /**
     * Is the band letter a Svalbard GZD (X)
     *
     * @param bandLetter
     *            band letter
     * @return true if a Svalbard GZD
     */
    static isSvalbardLetter(bandLetter) {
        return bandLetter === MGRSConstants_1.MGRSConstants.SVALBARD_BAND_LETTER;
    }
    /**
     * Is the zone number a Svalbard GZD (31 - 37)
     *
     * @param zoneNumber
     *            zone number
     * @return true if a Svalbard GZD
     */
    static isSvalbardZone(zoneNumber) {
        return zoneNumber >= MGRSConstants_1.MGRSConstants.MIN_SVALBARD_ZONE_NUMBER && zoneNumber <= MGRSConstants_1.MGRSConstants.MAX_SVALBARD_ZONE_NUMBER;
    }
    /**
     * Get the Svalbard longitudinal strip from the strip
     *
     * @param strip
     *            longitudinal strip
     * @return Svalbard strip or null for empty strips
     */
    static getSvalbardStrip(strip) {
        let svalbardStrip;
        const stripNumber = strip.getNumber();
        if (stripNumber % 2 === 1) {
            let west = strip.getWest();
            let east = strip.getEast();
            const halfWidth = (east - west) / 2.0;
            if (stripNumber > 31) {
                west -= halfWidth;
            }
            if (stripNumber < 37) {
                east += halfWidth;
            }
            svalbardStrip = new LongitudinalStrip_1.LongitudinalStrip(stripNumber, west, east);
        }
        return svalbardStrip;
    }
    /**
     * Get the Svalbard zone number from the longitude
     *
     * @param longitude
     *            longitude
     * @return zone number
     */
    static getSvalbardZone(longitude) {
        const minimumLongitude = this.getWestLongitude(MGRSConstants_1.MGRSConstants.MIN_SVALBARD_ZONE_NUMBER);
        const zoneValue = MGRSConstants_1.MGRSConstants.MIN_SVALBARD_ZONE_NUMBER + (longitude - minimumLongitude) / MGRSConstants_1.MGRSConstants.ZONE_WIDTH;
        let zone = ~~Math.round(zoneValue);
        if (zone % 2 === 0) {
            zone--;
        }
        return zone;
    }
    /**
     * Is the zone number and band letter a Norway GZD (31V or 32V)
     *
     * @param zoneNumber
     *            zone number
     * @param bandLetter
     *            band letter
     * @return true if a Norway GZD
     */
    static isNorway(zoneNumber, bandLetter) {
        return this.isNorwayLetter(bandLetter) && this.isNorwayZone(zoneNumber);
    }
    /**
     * Is the band letter a Norway GZD (V)
     *
     * @param bandLetter
     *            band letter
     * @return true if a Norway GZD band letter
     */
    static isNorwayLetter(bandLetter) {
        return bandLetter === MGRSConstants_1.MGRSConstants.NORWAY_BAND_LETTER;
    }
    /**
     * Is the zone number a Norway GZD (31 or 32)
     *
     * @param zoneNumber
     *            zone number
     * @return true if a Norway GZD zone number
     */
    static isNorwayZone(zoneNumber) {
        return zoneNumber >= MGRSConstants_1.MGRSConstants.MIN_NORWAY_ZONE_NUMBER && zoneNumber <= MGRSConstants_1.MGRSConstants.MAX_NORWAY_ZONE_NUMBER;
    }
    /**
     * Get the Norway longitudinal strip from the strip
     *
     * @param strip
     *            longitudinal strip
     * @return Norway strip
     */
    static getNorwayStrip(strip) {
        const stripNumber = strip.getNumber();
        let west = strip.getWest();
        let east = strip.getEast();
        const halfWidth = (east - west) / 2.0;
        let expand = 0;
        if (stripNumber === MGRSConstants_1.MGRSConstants.MIN_NORWAY_ZONE_NUMBER) {
            east -= halfWidth;
            expand++;
        }
        else if (stripNumber === MGRSConstants_1.MGRSConstants.MAX_NORWAY_ZONE_NUMBER) {
            west -= halfWidth;
        }
        return new LongitudinalStrip_1.LongitudinalStrip(stripNumber, west, east, expand);
    }
    /**
     * Get the Norway zone number from the longitude
     *
     * @param longitude
     *            longitude
     * @return zone number
     */
    static getNorwayZone(longitude) {
        const minimumLongitude = this.getWestLongitude(MGRSConstants_1.MGRSConstants.MIN_NORWAY_ZONE_NUMBER);
        let zone = MGRSConstants_1.MGRSConstants.MIN_NORWAY_ZONE_NUMBER;
        if (longitude >= minimumLongitude + MGRSConstants_1.MGRSConstants.ZONE_WIDTH / 2.0) {
            zone++;
        }
        return zone;
    }
}
exports.GridZones = GridZones;
_a = GridZones;
/**
 * Longitudinal Strips
 */
GridZones.strips = new Map();
/**
 * Latitude Bands
 */
GridZones.bands = new Map();
/**
 * Grid Zones
 */
GridZones.gridZones = new Map();
(() => {
    // Create longitudinal strips
    const numberRange = new ZoneNumberRange_1.ZoneNumberRange();
    for (const zoneNumber of numberRange) {
        const longitude = MGRSConstants_1.MGRSConstants.MIN_LON + (zoneNumber - 1) * MGRSConstants_1.MGRSConstants.ZONE_WIDTH;
        const strip = new LongitudinalStrip_1.LongitudinalStrip(zoneNumber, longitude, longitude + MGRSConstants_1.MGRSConstants.ZONE_WIDTH);
        _a.strips.set(strip.getNumber(), strip);
    }
    // Create latitude bands
    let latitude = MGRSConstants_1.MGRSConstants.MIN_LAT;
    const letterRange = new BandLetterRange_1.BandLetterRange();
    for (const bandLetter of letterRange) {
        const min = latitude;
        if (bandLetter === MGRSConstants_1.MGRSConstants.MAX_BAND_LETTER) {
            latitude += MGRSConstants_1.MGRSConstants.MAX_BAND_HEIGHT;
        }
        else {
            latitude += MGRSConstants_1.MGRSConstants.BAND_HEIGHT;
        }
        _a.bands.set(bandLetter, new LatitudeBand_1.LatitudeBand(bandLetter, min, latitude));
    }
    // Create grid zones
    for (const strip of GridZones.strips.values()) {
        const zoneNumber = strip.getNumber();
        const stripGridZones = new Map();
        for (const band of GridZones.bands.values()) {
            const bandLetter = band.getLetter();
            let gridZoneStrip = strip;
            if (_a.isSvalbard(zoneNumber, bandLetter)) {
                gridZoneStrip = _a.getSvalbardStrip(strip);
            }
            else if (_a.isNorway(zoneNumber, bandLetter)) {
                gridZoneStrip = _a.getNorwayStrip(strip);
            }
            if (gridZoneStrip) {
                stripGridZones.set(bandLetter, new GridZone_1.GridZone(gridZoneStrip, band));
            }
        }
        _a.gridZones.set(zoneNumber, stripGridZones);
    }
})();
//# sourceMappingURL=GridZones.js.map